import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { RoutePaths } from '../../../constants';
import { selectCurrentUser } from '../../../store/auth/selectors';
import { selectOnlineUsers } from '../../../store/users/selectors';
import { PatientDetailsMenu } from '../PatientDetailsMenu/PatientDetailsMenu';
import { PatientDiscussion } from '../components/PatientDiscussion/PatientDiscussion';
import { PatientProfile } from '../components/PatientProfile/PatientProfile';
import { PatientsSection } from '../components/PatientsSection/PatientsSection';
import styles from './MobileContainer.module.scss';
import cx from 'classnames';
import classNames from 'classnames';
import { BlockWrapperWithSpinner, Button, Icon, NoContent, RolePermissionChecker, Text } from 'components';
import { selectCurrentPatientTab, selectShowPatientCaseList } from 'store/app/selectors';
import { CurrentPatientTab, appActions } from 'store/app/slice';
import { dashboardActions } from 'store/dashboard/slice';
import { selectCurrentPatientCase, selectCurrentPatientCaseIsLoading } from 'store/patients/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { UserRole } from 'types';

export const tabs: CurrentPatientTab[] = ['information', 'discussion'];

export const MobileContainer = () => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { patientId } = useParams();

  const location = useLocation();

  const [containerHeight, setContainerHeight] = useState(0);

  const patientListIsShown = useSelector(selectShowPatientCaseList);

  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const currentPatientTab = useSelector(selectCurrentPatientTab);

  const isCaseLoading = useSelector(selectCurrentPatientCaseIsLoading);

  const currentUser = useSelector(selectCurrentUser);
  const onlineUsers = useSelector(selectOnlineUsers);

  const isDoctorOnline = onlineUsers.includes(currentPatientCase?.assignedDoctorId || '');
  const isClinicOnline = onlineUsers.includes(currentPatientCase?.clinicId || '');
  const currentRole = currentUser?.role;
  const isOtherUserOnline = currentRole === UserRole.CLINIC ? isDoctorOnline : isClinicOnline;
  const patientCaseId = currentPatientCase?.id;

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.scrollHeight);
    }
  }, [currentPatientCase, patientListIsShown]);

  useEffect(() => {
    if (location.pathname === RoutePaths.PATIENTS && !patientListIsShown) {
      dispatch(appActions.setShowPatientCaseList(true));
    }
    if (
      (location.pathname === `${RoutePaths.PATIENTS}/${currentPatientCase?.id}` ||
        location.pathname === `${RoutePaths.PATIENTS}/${patientId}`) &&
      patientListIsShown
    ) {
      dispatch(appActions.setShowPatientCaseList(false));
    }
  }, [location]);

  useEffect(() => {
    if (patientListIsShown) {
      dispatch(appActions.setCurrentPatientTab('information'));
    }
  }, [patientListIsShown, dispatch, location.search, navigate]);

  const rolesAllowedToEdit = currentPatientCase?.isArchived ? [UserRole.ADMIN] : [UserRole.CLINIC, UserRole.ADMIN];

  const showPatientCaseList = () => {
    dispatch(appActions.setShowPatientCaseList(true));
    navigate({
      pathname: RoutePaths.PATIENTS,
      search: location.search,
    });
  };

  const goToPatients = () => {
    navigate(RoutePaths.PATIENTS);
    showPatientCaseList();
  };

  const handleBackButton = () => {
    if (location.state?.path === RoutePaths.DASHBOARD) {
      navigate(RoutePaths.DASHBOARD);
    } else {
      showPatientCaseList();
    }
  };

  const onDismissPatientCase = async () => {
    try {
      if (!patientCaseId) return;
      await dispatch(dashboardActions.dismissPatientCase(patientCaseId)).unwrap();
    } catch {
      console.log('failed to dismiss ', patientCaseId);
    }
  };

  if (isCaseLoading && !patientListIsShown) return <BlockWrapperWithSpinner isLoading />;

  if (!currentPatientCase && !patientListIsShown)
    return (
      <div className={styles.noContentWrapper}>
        <NoContent pageKey="patientProfile" className={styles.noContent} hideIcon />
        <Button onClick={goToPatients}>Show patients</Button>
      </div>
    );

  const showOnlineStatusLabel =
    currentPatientCase?.assignedDoctorId &&
    (currentPatientCase?.assignedDoctorId === currentUser?.id || currentPatientCase?.clinicId === currentUser?.id);

  return (
    <div className={styles.container}>
      {patientListIsShown ? (
        <div className={styles.patientsSection}>
          <PatientsSection />
        </div>
      ) : (
        <div className={styles.patientInfo}>
          <div className={styles.header} ref={containerRef}>
            <div className={styles.headerInfo}>
              <button className={styles.backButton}>
                <Icon name="ArrowLeft" onClick={handleBackButton} />
              </button>
              <div className={styles.titlesWrapper}>
                <Text type="h2" className={styles.title}>
                  Patient
                </Text>
                <Text type="caption" className={styles.name}>
                  {`${currentPatientCase?.firstName} ${currentPatientCase?.lastName}`}
                </Text>
                {showOnlineStatusLabel && (
                  <div
                    className={cx(
                      styles.onlineStatusContainer,
                      isOtherUserOnline ? styles.onlineStatusContainerOnline : styles.onlineStatusContainerOffline,
                    )}>
                    <div className={styles.onlineStatusDot} />
                    <Text type="caption">{isOtherUserOnline ? 'Online' : 'Away'}</Text>
                  </div>
                )}
                <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC, UserRole.DOCTOR, UserRole.ADMIN]}>
                  <div
                    onClick={onDismissPatientCase}
                    className={classNames(
                      {
                        [styles.visible]: currentPatientTab === 'discussion',
                      },
                      styles.dismissWrapper,
                    )}>
                    <Icon name="Cross" className={styles.icon} />
                    <Text type="body2">Dismiss message notification</Text>
                  </div>
                </RolePermissionChecker>
              </div>
              <div className={styles.menuButtonContainer}>
                <RolePermissionChecker rolesAllowedToView={rolesAllowedToEdit}>
                  <PatientDetailsMenu />
                </RolePermissionChecker>
              </div>
            </div>
            <div className={styles.headerTabs}>
              {tabs.map((name) => (
                <div
                  key={name}
                  className={cx(styles.tab, { [styles.isActive]: currentPatientTab === name })}
                  onClick={() => dispatch(appActions.setCurrentPatientTab(name))}>
                  <Text type="overline">{name}</Text>
                </div>
              ))}
            </div>
          </div>
          {currentPatientTab === 'information' && <PatientProfile />}
          {currentPatientTab === 'discussion' && <PatientDiscussion height={containerHeight} />}
        </div>
      )}
    </div>
  );
};
